import React, { useEffect, useState } from 'react';
import { contactsApi } from '../api/client';
import type { Contact } from '../api/client';
import './Contacts.css';

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    category: 'friend' as 'family' | 'friend' | 'professional' | 'emergency' | 'business',
    company: '',
    jobTitle: '',
    email: '',
    phone: '',
    birthday: '',
    notes: '',
    tags: ''
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactsApi.getContacts();
      setContacts(response.data);
    } catch (err) {
      setError('Failed to load contacts');
      console.error('Contacts error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.firstName.trim()) return;

    try {
      const contactData = {
        firstName: newContact.firstName,
        lastName: newContact.lastName || undefined,
        displayName: newContact.displayName || `${newContact.firstName} ${newContact.lastName}`.trim(),
        category: newContact.category,
        company: newContact.company || undefined,
        jobTitle: newContact.jobTitle || undefined,
        birthday: newContact.birthday || undefined,
        notes: newContact.notes || undefined,
        tags: newContact.tags ? newContact.tags.split(',').map(t => t.trim()).filter(t => t) : undefined,
        emails: newContact.email ? [{
          email: newContact.email,
          type: 'personal' as 'personal' | 'work' | 'other',
          primary: true
        }] : undefined,
        phones: newContact.phone ? [{
          number: newContact.phone,
          type: 'mobile' as 'mobile' | 'home' | 'work' | 'fax' | 'other',
          primary: true
        }] : undefined
      };

      await contactsApi.createContact(contactData);
      await fetchContacts();
      setShowAddModal(false);
      resetNewContact();
    } catch (err) {
      console.error('Error adding contact:', err);
      setError('Failed to add contact');
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await contactsApi.deleteContact(id);
      await fetchContacts();
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError('Failed to delete contact');
    }
  };

  const resetNewContact = () => {
    setNewContact({
      firstName: '',
      lastName: '',
      displayName: '',
      category: 'friend',
      company: '',
      jobTitle: '',
      email: '',
      phone: '',
      birthday: '',
      notes: '',
      tags: ''
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'family': return '👨‍👩‍👧‍👦';
      case 'friend': return '👥';
      case 'professional': return '💼';
      case 'business': return '🏢';
      case 'emergency': return '🚨';
      default: return '👤';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'family': return '#e74c3c';
      case 'friend': return '#3498db';
      case 'professional': return '#2ecc71';
      case 'business': return '#f39c12';
      case 'emergency': return '#e67e22';
      default: return '#95a5a6';
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesCategory = filterCategory === 'all' || contact.category === filterCategory;
    const matchesSearch = !searchTerm || 
      contact.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.emails?.some(email => email.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return <div className="contacts-page"><div className="loading">Loading contacts...</div></div>;
  }

  if (error) {
    return <div className="contacts-page"><div className="error">Error: {error}</div></div>;
  }

  const categories = ['all', 'family', 'friend', 'professional', 'business', 'emergency'];

  return (
    <div className="contacts-page">
      <header className="page-header">
        <h1>📞 Contacts</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Contact
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="contacts-filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="category-filters">
          {categories.map(category => (
            <button
              key={category}
              className={`category-filter ${filterCategory === category ? 'active' : ''}`}
              onClick={() => setFilterCategory(category)}
            >
              {category === 'all' ? 'All' : category.charAt(0).toUpperCase() + category.slice(1)}
              {category !== 'all' && (
                <span className="category-count">
                  {contacts.filter(c => c.category === category).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="contacts-stats">
        <div className="stat-card">
          <span className="stat-number">{filteredContacts.length}</span>
          <span className="stat-label">
            {filterCategory === 'all' ? 'Total Contacts' : `${filterCategory} Contacts`}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{contacts.filter(c => c.company).length}</span>
          <span className="stat-label">With Company</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{contacts.filter(c => c.birthday).length}</span>
          <span className="stat-label">With Birthday</span>
        </div>
      </div>

      <div className="contacts-grid">
        {filteredContacts.map((contact) => (
          <div key={contact.id} className="contact-card">
            <div className="contact-header">
              <div className="contact-avatar">
                <span className="avatar-icon">{getCategoryIcon(contact.category)}</span>
              </div>
              <div className="contact-info">
                <h3 className="contact-name">{contact.displayName || `${contact.firstName} ${contact.lastName || ''}`.trim()}</h3>
                <span 
                  className="contact-category"
                  style={{ backgroundColor: getCategoryColor(contact.category) }}
                >
                  {contact.category}
                </span>
              </div>
              <button 
                className="delete-btn"
                onClick={() => handleDeleteContact(contact.id)}
                title="Delete contact"
              >
                ×
              </button>
            </div>

            <div className="contact-content">
              {contact.company && (
                <div className="contact-detail">
                  <span className="detail-icon">🏢</span>
                  <div className="detail-text">
                    <strong>{contact.company}</strong>
                    {contact.jobTitle && <span> - {contact.jobTitle}</span>}
                  </div>
                </div>
              )}

              {contact.emails && contact.emails.length > 0 && (
                <div className="contact-detail">
                  <span className="detail-icon">📧</span>
                  <div className="detail-text">
                    {contact.emails.map((email, index) => (
                      <div key={index}>
                        <a href={`mailto:${email.email}`} className="contact-link">
                          {email.email}
                        </a>
                        <span className="contact-type">({email.type})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contact.phones && contact.phones.length > 0 && (
                <div className="contact-detail">
                  <span className="detail-icon">📱</span>
                  <div className="detail-text">
                    {contact.phones.map((phone, index) => (
                      <div key={index}>
                        <a href={`tel:${phone.number}`} className="contact-link">
                          {phone.number}
                        </a>
                        <span className="contact-type">({phone.type})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {contact.birthday && (
                <div className="contact-detail">
                  <span className="detail-icon">🎂</span>
                  <div className="detail-text">
                    {new Date(contact.birthday).toLocaleDateString()}
                  </div>
                </div>
              )}

              {contact.notes && (
                <div className="contact-notes">
                  <span className="notes-icon">📝</span>
                  <p className="notes-text">{contact.notes}</p>
                </div>
              )}

              {contact.tags && contact.tags.length > 0 && (
                <div className="contact-tags">
                  {contact.tags.map((tag, index) => (
                    <span key={index} className="contact-tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {filteredContacts.length === 0 && (
          <div className="no-contacts">
            <span className="no-contacts-icon">📞</span>
            <p>{searchTerm || filterCategory !== 'all' 
              ? 'No contacts match your search criteria' 
              : 'No contacts added yet. Add your first contact to get started!'}</p>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Contact</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newContact.firstName}
                    onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                    className="form-input"
                    placeholder="Enter first name..."
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={newContact.lastName}
                    onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                    className="form-input"
                    placeholder="Enter last name..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={newContact.category}
                    onChange={(e) => setNewContact({...newContact, category: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="friend">Friend</option>
                    <option value="family">Family</option>
                    <option value="professional">Professional</option>
                    <option value="business">Business</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={newContact.displayName}
                    onChange={(e) => setNewContact({...newContact, displayName: e.target.value})}
                    className="form-input"
                    placeholder="Optional display name..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    className="form-input"
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="form-input"
                    placeholder="+1-555-123-4567"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    value={newContact.company}
                    onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                    className="form-input"
                    placeholder="Company name..."
                  />
                </div>

                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={newContact.jobTitle}
                    onChange={(e) => setNewContact({...newContact, jobTitle: e.target.value})}
                    className="form-input"
                    placeholder="Job title..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Birthday</label>
                  <input
                    type="date"
                    value={newContact.birthday}
                    onChange={(e) => setNewContact({...newContact, birthday: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={newContact.tags}
                    onChange={(e) => setNewContact({...newContact, tags: e.target.value})}
                    className="form-input"
                    placeholder="colleague, tech, college..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newContact.notes}
                  onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Additional notes about this contact..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddContact}
                disabled={!newContact.firstName.trim()}
              >
                Add Contact
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;